import React, { useMemo, useRef, useState } from 'react';
import { MODULE_CONFIG_TABS } from './moduleConfigTabs';

// Cross-tab settings finder. Indexes every field label (base tabs hand-listed,
// module tabs derived from their defs) and jumps to the owning tab on select.

interface IndexEntry { label: string; tabId: string; tabLabel: string; }

const BASE_FIELDS: IndexEntry[] = [
  ...['Unit Cost', 'Expected Utilization', 'Hardware Profit Margin', 'Monthly Rental Rate']
    .map((label) => ({ label, tabId: 'hardware', tabLabel: 'Hardware' })),
  ...['Development Cost', 'Target Recovery Period', 'Expected Units/Year', 'Target Profit Margin']
    .map((label) => ({ label, tabId: 'application', tabLabel: 'Application' })),
  ...['0-12 Months (Standard Rate)', '13-24 Months Discount', '25-36 Months Discount', '37-60 Months Discount', 'Perpetual License Discount']
    .map((label) => ({ label, tabId: 'licensing', tabLabel: 'Duration Discount' })),
  ...['Connectivity (per machine/month)', 'Cloud Compute (per machine/month)', 'Cloud Storage (per machine/month)',
    'Live Mode Internet/Cloud Multiplier', 'Offline Mode Internet/Cloud Multiplier', 'Default Spare Charging Mode',
    'Allow Spare Mode Override in Proposal', 'Hourly Support Rate', 'Training Hourly Rate', 'Training Starter Session Hours',
    'Training Standard Session Hours', 'Training Full Day Session Hours', 'Provisioning Hourly Rate', 'On-field Support Daily Rate',
    'Unit Weight', 'Shipping Cost per kg']
    .map((label) => ({ label, tabId: 'costs', tabLabel: 'Other Costs' })),
];

const INDEX: IndexEntry[] = [
  ...BASE_FIELDS,
  ...MODULE_CONFIG_TABS.flatMap((t) => t.fields.map((fd) => ({ label: fd.label, tabId: t.id, tabLabel: t.label }))),
];

const MAX_RESULTS = 12;

export const SettingsSearch: React.FC<{ onSelect: (tabId: string) => void }> = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return INDEX.filter((e) => e.label.toLowerCase().includes(q) || e.tabLabel.toLowerCase().includes(q));
  }, [query]);

  const pick = (tabId: string) => { onSelect(tabId); setQuery(''); setFocused(false); };

  const open = focused && query.trim().length > 0;
  return (
    <div className="relative w-full max-w-md">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setFocused(false), 120); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); (e.target as HTMLInputElement).blur(); } if (e.key === 'Enter' && matches[0]) pick(matches[0].tabId); }}
        placeholder="🔍  Search settings across all tabs…"
        className="block w-full rounded-lg border border-[#E2E5EA] bg-white pl-3 pr-3 py-2 text-[13px] text-text-grey focus:border-hero-red focus:ring-1 focus:ring-hero-red"
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-[#E2E5EA] bg-white shadow-[0_8px_24px_rgba(0,39,63,0.12)] overflow-hidden">
          {matches.length === 0 ? (
            <div className="px-3 py-3 text-[13px] text-heritage-grey">No settings match “{query}”.</div>
          ) : (
            <>
              {matches.slice(0, MAX_RESULTS).map((e, i) => (
                <button
                  key={`${e.tabId}-${e.label}-${i}`}
                  onMouseDown={() => pick(e.tabId)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-hero-red-10"
                >
                  <span className="text-[13px] text-blueprint-blue">{e.label}</span>
                  <span className="shrink-0 text-[11px] text-heritage-grey">{e.tabLabel}</span>
                </button>
              ))}
              {matches.length > MAX_RESULTS && (
                <div className="px-3 py-2 text-[11px] text-heritage-grey border-t border-[#EEF0F3]">+{matches.length - MAX_RESULTS} more — keep typing to narrow.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
